import { QUICK_SEARCH_MIN_LENGTH } from './quickSearchConfig';
import { normalizeWildcardInput } from './animalIdentChipFilter';

export const WildcardKind = {
  contains: 'contains',
  endsWith: 'endsWith',
  pattern: 'pattern',
  startsWith: 'startsWith',
} as const;

export type WildcardKind = (typeof WildcardKind)[keyof typeof WildcardKind];

const WILDCARD_LABEL_PREFIX: Record<WildcardKind, string> = {
  [WildcardKind.contains]: 'Contains',
  [WildcardKind.endsWith]: 'Ends with',
  [WildcardKind.pattern]: 'Matches pattern',
  [WildcardKind.startsWith]: 'Starts with',
};

export type WildcardOption = {
  _isWildcard: true;
  kind: WildcardKind;
  pattern: string;
  displayBase: string;
};

/** Non-selectable informational item shown when the dropdown would otherwise be empty. */
export type MessageOption = { _isMessage: true; text: string };

export function isMessageOption(v: unknown): v is MessageOption {
  return (
    typeof v === 'object' &&
    v !== null &&
    (v as MessageOption)._isMessage === true
  );
}

export function isWildcardOption(v: unknown): v is WildcardOption {
  return (
    typeof v === 'object' &&
    v !== null &&
    (v as WildcardOption)._isWildcard === true
  );
}

export function resolveWildcard(term: string): WildcardOption | null {
  const t = normalizeWildcardInput(term).trim();
  if (!t) return null;
  const leading = t.startsWith('%');
  const trailing = t.endsWith('%');
  const base = t.replace(/^%+|%+$/g, '').trim();
  if (base.length < QUICK_SEARCH_MIN_LENGTH) return null;
  if (base.includes('%')) {
    return {
      _isWildcard: true,
      kind: WildcardKind.pattern,
      pattern: t,
      displayBase: t,
    };
  }
  if (leading && trailing) {
    return {
      _isWildcard: true,
      kind: WildcardKind.contains,
      pattern: `%${base}%`,
      displayBase: base,
    };
  }
  if (leading) {
    return {
      _isWildcard: true,
      kind: WildcardKind.endsWith,
      pattern: `%${base}`,
      displayBase: base,
    };
  }
  return {
    _isWildcard: true,
    kind: WildcardKind.startsWith,
    pattern: `${base}%`,
    displayBase: base,
  };
}

export function resolveBlurWildcardPattern(
  term: string,
  plainTextBlurWildcard?: WildcardKind,
): string | undefined {
  const normalized = normalizeWildcardInput(term).trim();
  if (!normalized) return undefined;
  if (
    plainTextBlurWildcard === WildcardKind.contains &&
    !normalized.includes('%')
  ) {
    if (normalized.length < QUICK_SEARCH_MIN_LENGTH) return undefined;
    return `%${normalized}%`;
  }
  return resolveWildcard(normalized)?.pattern;
}

export function resolveExplicitWildcards(term: string): WildcardOption[] {
  const t = normalizeWildcardInput(term).trim();
  if (!t || t.includes('%')) return [];
  if (t.length < QUICK_SEARCH_MIN_LENGTH) return [];
  return [
    {
      _isWildcard: true,
      kind: WildcardKind.startsWith,
      pattern: `${t}%`,
      displayBase: t,
    },
    {
      _isWildcard: true,
      kind: WildcardKind.contains,
      pattern: `%${t}%`,
      displayBase: t,
    },
    {
      _isWildcard: true,
      kind: WildcardKind.endsWith,
      pattern: `%${t}`,
      displayBase: t,
    },
  ];
}

export function getWildcardLabel({
  kind,
  displayBase,
}: WildcardOption): string {
  return `${WILDCARD_LABEL_PREFIX[kind]} \u201C${displayBase}\u201D`;
}
